import { Link } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import { Check, ShoppingCart, BookOpen, Play, Award, ArrowRight, Star, ShieldCheck, Download } from 'lucide-react';

const PRICE = 97;
const SLUG = 'forex-made-simple';

const CURRICULUM = [
  { num: '01', title: 'Introduction' },
  { num: '02', title: 'Lesson 1 — What is Forex?' },
  { num: '03', title: 'Lesson 2 — Best Trading Times' },
  { num: '04', title: 'Lesson 3 — MT4 Setup (Part 1)' },
  { num: '05', title: 'Lesson 3 — MT4 Setup (Part 2)' },
  { num: '06', title: 'Lesson 3 — MT4 Setup (Part 3)' },
  { num: '07', title: 'Lesson 4 — Pips and Pipettes' },
  { num: '08', title: 'Lesson 5 — Lots and Lot Sizes' },
  { num: '09', title: 'Lesson 6 — Candlesticks: Design (Part 1)' },
  { num: '10', title: 'Lesson 6 — Candle Formations (Part 2)' },
  { num: '11', title: 'Lesson 7 — Fundamentals' },
  { num: '12', title: 'Lesson 12 — Stochastic' },
  { num: '13', title: 'Lesson 13 — Moving Averages' },
  { num: '14', title: 'Lesson 14 — Trend Analysis' },
  { num: '15', title: 'Lesson 15 — Support and Resistance' },
  { num: '16', title: 'Lesson 16 — Strategy 1' },
  { num: '17', title: 'Lesson 17 — Strategy 2' },
  { num: '18', title: 'Lesson 18 — Strategy 3' },
  { num: '19', title: 'How to Place a Trade' },
];

const TOTAL_DURATION = 'Full beginner curriculum';

const WHATS_INCLUDED = [
  { icon: Play, label: '19 video lessons', sub: 'Full beginner curriculum' },
  { icon: BookOpen, label: '3 training workbooks (PDF)', sub: 'Test and reinforce your knowledge' },
  { icon: Download, label: 'Quick start files', sub: 'Templates, indicators, MT4 setup pack' },
  { icon: Award, label: 'Lifetime access', sub: 'Watch on any device, forever' },
];

const FAQ = [
  { q: 'Do I need any trading experience?', a: 'No. Forex Made Simple is built from the ground up — by the end of the course you will understand the market, the platform, candles, indicators, and have three complete strategies you can apply immediately.' },
  { q: 'Do I need to buy any extra software?', a: 'No. MetaTrader 4 is free, and the course walks you through downloading and setting it up step by step.' },
  { q: 'Is this a prerequisite for the other courses?', a: 'Yes. Forex Made Simple is the foundation Hettie recommends before moving on to the 9-Step Day Trading System or Bowtie Scalping Strategy.' },
  { q: "What's the refund policy?", a: '30-day money back guarantee — no questions asked.' },
];

export default function ForexMadeSimplePage() {
  const { addItem, items } = useCart();
  const inCart = items.some(i => i.slug === SLUG);
  const addToCart = () => addItem({ slug: SLUG, name: 'Forex Made Simple', price: PRICE, image_url: '', platform: 'Course' });

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="forex-made-simple-page">
      {/* Hero */}
      <section className="px-4 sm:px-6 lg:px-8 pb-12 relative overflow-hidden" data-testid="fms-hero">
        <div className="absolute top-0 left-1/3 w-[600px] h-[600px] rounded-full bg-emerald-400/[0.04] blur-[150px] pointer-events-none" />
        <div className="relative mx-auto max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3">Beginner Course — Start Here</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight">
              Forex <span className="text-emerald-400">Made Simple</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-white/55 leading-relaxed max-w-xl">
              The complete beginner's roadmap to forex — from your first chart to your first confident live trade. Built on 20+ years of one-on-one teaching by Hettie Nel.
            </p>
            <div className="mt-7 flex items-center gap-6 text-xs text-white/40">
              <span className="flex items-center gap-1.5"><Play size={13} className="text-emerald-400" /> 19 lessons</span>
              <span className="flex items-center gap-1.5"><BookOpen size={13} className="text-emerald-400" /> 3 workbooks</span>
              <span className="flex items-center gap-1.5"><Award size={13} className="text-emerald-400" /> Lifetime access</span>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                onClick={addToCart}
                disabled={inCart}
                className={`inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-bold transition-all ${inCart ? 'bg-white/10 text-white/40 cursor-default' : 'bg-emerald-400 text-black hover:bg-emerald-300 hover:scale-[1.02]'}`}
                data-testid="fms-add-to-cart-hero"
              >
                <ShoppingCart size={16} /> {inCart ? 'In Cart' : `Enroll Now — $${PRICE}`}
              </button>
              <Link to="/cart" className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white transition-colors" data-testid="fms-go-to-cart">
                Go to Cart <ArrowRight size={14} />
              </Link>
            </div>
            <p className="mt-3 text-[11px] text-white/30">30-day money back guarantee • One-time payment • Instant access</p>
          </div>

          {/* Price card / side panel */}
          <div className="relative" data-testid="fms-price-card">
            <div className="rounded-2xl border border-emerald-400/30 bg-emerald-400/[0.03] p-8 ring-1 ring-emerald-400/10">
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-bold text-white">${PRICE}</span>
                <span className="text-sm text-white/30">one-time</span>
              </div>
              <p className="mt-1 text-xs text-white/40">Lifetime access — no subscriptions</p>

              <ul className="mt-6 space-y-3">
                {WHATS_INCLUDED.map((it, i) => {
                  const Icon = it.icon;
                  return (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-emerald-400/10 flex items-center justify-center shrink-0">
                        <Icon size={16} className="text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{it.label}</p>
                        <p className="text-xs text-white/40">{it.sub}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>

              <button
                onClick={addToCart}
                disabled={inCart}
                className={`mt-7 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-sm font-bold transition-colors ${inCart ? 'bg-white/5 text-white/30' : 'bg-emerald-400 text-black hover:bg-emerald-300'}`}
                data-testid="fms-add-to-cart-side"
              >
                <ShoppingCart size={15} /> {inCart ? 'In Cart' : 'Enroll Now'}
              </button>
              <div className="mt-4 flex items-center gap-2 text-[11px] text-white/40">
                <ShieldCheck size={13} className="text-emerald-400" /> 30-day money back guarantee
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who this is for */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 border-t border-white/[0.04]" data-testid="fms-who-for">
        <div className="mx-auto max-w-5xl grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-400 mb-3">Who this is for</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Built for traders who want to do it right the first time</h2>
            <p className="mt-4 text-sm text-white/55 leading-relaxed">
              Most traders lose money in their first year because they skip the fundamentals. Forex Made Simple is the curriculum Hettie has refined over 20+ years of one-on-one teaching — the exact foundation she gives every new student before they touch a live account.
            </p>
          </div>
          <ul className="space-y-3">
            {[
              'Brand new to forex and want a structured path',
              'Tried online tutorials and ended up more confused',
              'Want to understand the why, not just the how',
              'Plan to move on to day-trading or scalping later',
              'Prefer a calm, methodical teacher (not hype)',
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm text-white/70">
                <Check size={16} className="text-emerald-400 mt-0.5 shrink-0" /> {t}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Curriculum */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 border-t border-white/[0.04]" data-testid="fms-curriculum">
        <div className="mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-400 mb-3">What&apos;s inside</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">19 video lessons · {TOTAL_DURATION} · 3 training workbooks</h2>
            <p className="mt-3 text-sm text-white/45 max-w-xl mx-auto">Every lesson Hettie teaches her one-on-one beginners — bundled together at one simple price.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {CURRICULUM.map((m) => (
              <div key={m.num} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 flex items-center gap-4" data-testid={`fms-lesson-${m.num}`}>
                <span className="text-xs font-mono text-emerald-400/70 shrink-0">{m.num}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{m.title}</p>
                </div>
              </div>
            ))}
            <div className="rounded-xl border border-emerald-400/20 bg-emerald-400/[0.03] p-4 md:col-span-2" data-testid="fms-bonus">
              <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 mb-2">Includes — 3 Training Workbooks</p>
              <ul className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-white/55">
                <li className="flex items-center gap-2"><BookOpen size={11} className="text-emerald-400/70 shrink-0" /> Workbook — Training Session 1</li>
                <li className="flex items-center gap-2"><BookOpen size={11} className="text-emerald-400/70 shrink-0" /> Workbook — Training Session 2</li>
                <li className="flex items-center gap-2"><BookOpen size={11} className="text-emerald-400/70 shrink-0" /> Workbook — Training Session 3</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 border-t border-white/[0.04]" data-testid="fms-testimonial">
        <div className="mx-auto max-w-3xl text-center">
          <div className="flex justify-center gap-1 mb-5">
            {[...Array(5)].map((_, i) => <Star key={i} size={16} className="fill-amber-400 text-amber-400" />)}
          </div>
          <p className="text-lg sm:text-xl text-white/80 italic leading-relaxed font-light">
            "Hettie's Forex Made Simple course is the only one I've taken that actually starts from zero and gets you to a working strategy. I went from random clicking to placing structured trades within two weeks."
          </p>
          <p className="mt-5 text-sm font-bold text-emerald-400">Sarah K.</p>
          <p className="text-xs text-white/40">Forex Peace Army verified review</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 border-t border-white/[0.04]" data-testid="fms-faq">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight text-center mb-10">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <details key={i} className="group rounded-xl border border-white/[0.06] bg-white/[0.02] p-5" data-testid={`fms-faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer text-sm font-semibold text-white list-none">
                  {f.q}
                  <span className="text-emerald-400 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                </summary>
                <p className="mt-3 text-sm text-white/55 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 border-t border-white/[0.04] text-center" data-testid="fms-cta">
        <div className="mx-auto max-w-2xl">
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">Start your trading journey the right way</h2>
          <p className="mt-3 text-sm text-white/50">Lifetime access. 30-day money back guarantee. Instant download.</p>
          <button
            onClick={addToCart}
            disabled={inCart}
            className={`mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-bold transition-all ${inCart ? 'bg-white/10 text-white/40 cursor-default' : 'bg-emerald-400 text-black hover:bg-emerald-300 hover:scale-[1.02]'}`}
            data-testid="fms-add-to-cart-footer"
          >
            <ShoppingCart size={18} /> {inCart ? 'In Cart' : `Enroll for $${PRICE}`}
          </button>
          <p className="mt-4 text-[11px] text-white/30">Secure payment via Stripe</p>
        </div>
      </section>
    </div>
  );
}
